import { motion } from 'framer-motion';
import { useState } from 'react';

const BitLockerCard = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'script'>('overview');

  const scriptOutput = `PS C:\\Scripts> .\\BitLocker-Key-Existence-Checker.ps1

╔══════════════════════════════════════════════════════════════════╗
║   BitLocker Recovery Key Existence Checker for Entra ID          ║
║   Version 1.0 | Author: Özgür Çebi                               ║
╚══════════════════════════════════════════════════════════════════╝

[INFO] Connecting to Microsoft Graph API...
[SUCCESS] Connected to tenant: contoso.onmicrosoft.com

[INFO] Fetching all devices from Entra ID...
[INFO] Found 156 devices in total

[INFO] Checking BitLocker recovery keys...
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Device Name: DESKTOP-A7K2M9B
  ├─ Device ID: 7f3a2b1c-4d5e-6f7g-8h9i-0j1k2l3m4n5o
  ├─ OS: Windows 11 Pro
  ├─ BitLocker Status: ✓ Recovery Key Found
  └─ Key ID: BK-001-A7K2-2025

Device Name: LAPTOP-B3N8P1Q
  ├─ Device ID: 8g4b3c2d-5e6f-7g8h-9i0j-1k2l3m4n5o6p
  ├─ OS: Windows 10 Enterprise
  ├─ BitLocker Status: ✓ Recovery Key Found
  └─ Key ID: BK-002-B3N8-2025

Device Name: DESKTOP-C5R4T6W
  ├─ Device ID: 9h5c4d3e-6f7g-8h9i-0j1k-2l3m4n5o6p7q
  ├─ OS: Windows 11 Pro
  ├─ BitLocker Status: ✗ No Recovery Key
  └─ Warning: Device not protected

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

[SUMMARY]
  Total Devices Scanned: 156
  Devices with Recovery Keys: 142 (91.0%)
  Devices without Recovery Keys: 14 (9.0%)
  
[INFO] Exporting results to CSV...
[SUCCESS] Report saved: BitLocker-Report-2025-01-15.csv

[COMPLETED] Script execution finished in 23.4 seconds`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="mb-16 relative"
    >
      <div className="bg-gradient-to-br from-violet-50 via-white to-purple-50 rounded-2xl overflow-hidden shadow-2xl border-2 border-violet-200">
        {/* Header Section */}
        <div className="bg-gradient-to-br from-violet-600 via-purple-700 to-violet-800 px-8 py-8 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 20px, rgba(255,255,255,0.1) 20px, rgba(255,255,255,0.1) 40px)'
            }} />
          </div>

          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className="w-20 h-20 bg-white rounded-2xl shadow-2xl flex items-center justify-center">
                <span className="text-4xl">🔐</span>
              </div>
              <div>
                <div className="inline-block px-3 py-1 bg-amber-400/20 backdrop-blur-sm rounded-full mb-2">
                  <span className="text-amber-300 text-xs font-bold">ENTRA ID PROJECT</span>
                </div>
                <h3 className="text-3xl font-bold text-white mb-1">BitLocker Recovery Key Checker</h3>
                <p className="text-violet-200 text-sm">PowerShell Script for Microsoft Entra ID</p>
              </div>
            </div>

            <div className="text-right">
              <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg mb-2">
                <span className="text-white text-sm font-medium">PowerShell Script</span>
              </div>
              <div className="flex items-center justify-end space-x-4 mt-2">
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">v1.0</div>
                  <div className="text-xs text-violet-200">Latest</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-8">
          <p className="text-base text-gray-700 leading-relaxed mb-6">
            Microsoft Entra ID&apos;ye kayıtlı tüm cihazları tarayarak BitLocker recovery key&apos;lerinin varlığını kontrol eden PowerShell scripti. 
            Güvenlik denetimi ve compliance raporlaması için ideal araç.
          </p>

          {/* Features Grid */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="flex items-start space-x-3 p-4 bg-violet-50 rounded-lg border border-violet-100">
              <i className="ri-shield-check-line text-violet-600 text-xl mt-0.5"></i>
              <div>
                <h5 className="text-sm font-bold text-gray-900 mb-1">Otomatik Tarama</h5>
                <p className="text-xs text-gray-600">Tüm Entra ID cihazlarını otomatik tarar</p>
              </div>
            </div>
            <div className="flex items-start space-x-3 p-4 bg-violet-50 rounded-lg border border-violet-100">
              <i className="ri-file-list-3-line text-violet-600 text-xl mt-0.5"></i>
              <div>
                <h5 className="text-sm font-bold text-gray-900 mb-1">CSV Rapor</h5>
                <p className="text-xs text-gray-600">Detaylı CSV raporu oluşturur</p>
              </div>
            </div>
            <div className="flex items-start space-x-3 p-4 bg-violet-50 rounded-lg border border-violet-100">
              <i className="ri-line-chart-line text-violet-600 text-xl mt-0.5"></i>
              <div>
                <h5 className="text-sm font-bold text-gray-900 mb-1">İstatistikler</h5>
                <p className="text-xs text-gray-600">Kapsamlı güvenlik istatistikleri</p>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex items-center space-x-2 mb-4 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-2 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'overview'
                  ? 'text-violet-600 border-b-2 border-violet-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className="ri-information-line mr-1.5"></i>
              Özellikler
            </button>
            <button
              onClick={() => setActiveTab('script')}
              className={`px-4 py-2 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'script'
                  ? 'text-violet-600 border-b-2 border-violet-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className="ri-terminal-box-line mr-1.5"></i>
              Script Çıktısı
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-3">
              {[
                { icon: 'ri-search-line', text: 'Tüm Entra ID cihazlarını otomatik tarama' },
                { icon: 'ri-key-2-line', text: 'BitLocker recovery key varlık kontrolü' },
                { icon: 'ri-file-excel-2-line', text: 'Detaylı CSV raporu oluşturma' },
                { icon: 'ri-microsoft-line', text: 'Microsoft Graph API entegrasyonu' }
              ].map((feature, i) => (
                <div key={i} className="flex items-center space-x-3 text-sm text-gray-700">
                  <i className={`${feature.icon} text-violet-600`}></i>
                  <span>{feature.text}</span>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'script' && (
            <div className="bg-[#1E1E1E] rounded-xl overflow-hidden">
              <div className="flex items-center space-x-2 px-4 py-3 bg-[#2D2D30] border-b border-gray-700">
                <div className="flex space-x-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                </div>
                <span className="text-xs text-gray-400 ml-4">PowerShell Terminal</span>
              </div>
              <div className="p-4 font-mono text-[10px] leading-relaxed text-gray-300 max-h-96 overflow-y-auto">
                <pre className="whitespace-pre-wrap">{scriptOutput}</pre>
              </div>
            </div>
          )}

          {/* Technologies */}
          <div className="flex flex-wrap gap-2 mt-6 mb-6">
            <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">PowerShell</span>
            <span className="px-3 py-1 bg-cyan-100 text-cyan-700 text-xs font-medium rounded-full">Microsoft Graph API</span>
            <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">Entra ID</span>
            <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">BitLocker</span>
          </div>

          {/* Action Button */}
          <div className="flex items-center space-x-4">
            <a
              href="https://github.com/ozgurcebi/BitLocker-Key-Existence-Checker-for-Microsoft-Entra-ID"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="px-6 py-3 bg-violet-600 hover:bg-violet-700 text-white font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap shadow-lg shadow-violet-600/30"
            >
              <i className="ri-github-fill mr-2"></i>
              GitHub&apos;da Görüntüle
            </a>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default BitLockerCard;
